import { useContext, useState, useEffect, useRef } from "react";
import { UserContext } from "../../Contexts/UserContext";
import {
  Fab,
  FormControl,
  TextField,
  Box,
  Modal,
  Button,
} from "@material-ui/core";
import ExitToAppIcon from "@material-ui/icons/ExitToApp";
import { useMutation, useLazyQuery } from "@apollo/client";
import { LOGIN_MUTATION } from "../../Mutations/LoginMutation";
import { GET_ORIS_QUERY } from "../../Queries/GetOrisQuery";
import { Redirect } from "react-router-dom";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { localStoreData } from "../../utils/utils";
import { useCookies } from "react-cookie";
import { DashboardContext } from "../../Contexts/DashboardContext";
import HelpIconButton from "../HelpIconButton";
import { useStyles } from "../../Styles/styles";
import { ModalHeader } from "../ModalHeader";
import UserAgreement from "../UserAgreement";
import clsx from "clsx";
import { isOnlyBasicUser } from "./utils";
import { SetupContext } from "../../Contexts/SetupContext";
import CircularProgress from "@material-ui/core/CircularProgress";
import ChangePassword from "../ChangePassword/ChangePassword";

import moment from "moment";
import MP_logo from "../../Assets/MP_logo.png";

const styles = {
  container: {
    maxWidth: "75vw",
    flexGrow: 1,
    backgroundColor: "white",
    height: "fit-content",
  },
};

const Login = props => {
  const {
    updateAuthentication,
    updateUserInformation,
    updateErrorMessage,
    nightMode,
  } = useContext(UserContext);
  const { setupLoading } = useContext(SetupContext);
  const [showAgreement, setShowAgreement] = useState(false);
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [, setCookie, removeCookie] = useCookies();
  const [login, setLogin] = useState({
    userName: "",
    password: "",
    softToken: "",
  });
  const [oriValue, setOriValue] = useState(null);

  const classes = useStyles();

  const handleLoginResult = loginData => {
    if (!loginData || !loginData.login) {
      updateErrorMessage("An unknown error occurred during login.");
      return;
    }
    const errors = loginData?.login?.errors || [];
    const isExpired =
      !!loginData?.login?.loginState?.expiredPasswordInfo?.expiredMsg;
    if (errors.length === 0) {
      const userInfo = loginData.login.loginState;
      if (
        isOnlyBasicUser(userInfo) ||
        !userInfo?.roles ||
        userInfo?.roles?.length === 0
      ) {
        updateErrorMessage(
          "You do not have permission to access this application.",
        );
        return;
      }
      updateUserInformation(userInfo);
      loginData.login.authToken &&
        localStoreData(loginData.login.authToken, "mp_token", false);
      updateErrorMessage("");

      if (isExpired) {
        setShowPasswordChange(true);
        return;
      }
      setShowAgreement(true);
    } else {
      updateErrorMessage(errors?.join(" "));
    }
  };

  const [
    loginMutation,
    { data: loginData, loading: loginLoading, error: loginError },
  ] = useMutation(LOGIN_MUTATION, {
    context: { serializationKey: "incidentQueue" },
    onCompleted: loginData => handleLoginResult(loginData),
  });

  const queryString = window.location.search;
  const urlParams = new URLSearchParams(queryString);
  const arg = urlParams.get("mp_arg");

  useEffect(() => {
    if (arg) {
      loginMutation({
        variables: { input: { encryptedArg: arg } },
      });
    }
  }, [arg, loginMutation]);

  const handleAccept = () => {
    setShowAgreement(false);
    updateAuthentication(true);
  };

  const handleDecline = () => {
    setShowAgreement(false);
  };

  const onChangeHandler = e => {
    let name = e.target.name;
    let value = e.target.value;
    setLogin(prevState => {
      return { ...prevState, [name]: value };
    });
  };

  const handleOriChange = value => {
    if (value && value?.id) {
      setOriValue(value);
      removeCookie("mp-last-ori-selection");
      setCookie("mp-last-ori-selection", value.id, { path: "/mp" });
    }
  };

  const handleLogin = e => {
    e.preventDefault();
    let input = {
      oriId: oriValue?.id,
      login: login.userName,
      password: login.password,
      token: login.softToken,
    };
    if (!input.oriId) {
      alert("Please select an ORI");
    } else {
      loginMutation({
        variables: {
          input,
        },
      });
    }
  };

  const handleLoginWithNewPassword = () => {
    const userInfo = loginData?.login?.loginState;
    const authToken = loginData.login.authToken;
    if (userInfo) {
      updateUserInformation(userInfo);
      authToken && localStoreData(loginData.login.authToken, "mp_token", false);
      setShowAgreement(true);
      updateErrorMessage("");
    }
  };

  if (loginError)
    return (
      <div>There was an error logging in: {JSON.stringify(loginError)}</div>
    );
  if (setupLoading) {
    return (
      <div
        id="loginBackground"
        style={{
          backgroundColor: "#0ADDEE",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          width: "100%",
          alignItems: "center",
        }}
      >
        <img className={"logo"} alt="Mobile logo" src={MP_logo} />
        <CircularProgress />
        <p
          style={{
            color: "white",
            fontWeight: "bold",
            fontSize: "20px",
            fontFamily: "Roboto, Helvetica, Arial, sans-serif",
          }}
        >
          Loading Setup List data
        </p>
      </div>
    );
  }
  return (
    <>
      <LoginScreen
        {...props}
        arg={arg}
        loginMutation={loginMutation}
        loginLoading={loginLoading}
        login={login}
        onChangeHandler={onChangeHandler}
        oriValue={oriValue}
        setOriValue={setOriValue}
        handleOriChange={handleOriChange}
        handleLogin={handleLogin}
      />
      <ChangePassword
        showPasswordChange={showPasswordChange}
        closePasswordChange={setShowPasswordChange}
        login={login?.userName}
        currentPassword={login?.password}
        firstName={loginData?.login?.loginState?.firstName}
        lastName={loginData?.login?.loginState?.lastName}
        badge={loginData?.login?.loginState?.badge}
        userId={loginData?.login?.loginState?.userId}
        loginCallback={handleLoginWithNewPassword}
      />
      <Modal
        open={showAgreement}
        className={classes.modal}
        data-cy="user-agreement-modal"
        keepMounted={false}
      >
        <div style={styles.container}>
          <ModalHeader
            nightMode={nightMode}
            headerName={`Valcour User Agreement (${loginData?.login?.loginState?.expiredPasswordInfo?.expiresInMsg})`}
          />
          <div style={{ padding: "0.5em 1em" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Button
                type="button"
                className={clsx(classes.buttonHover, classes.navBarButtons)}
                style={{ width: "fit-content" }}
                variant="contained"
                onClick={handleAccept}
              >
                Accept
              </Button>
              <Button
                type="button"
                className={clsx(classes.buttonHover, classes.navBarButtons)}
                style={{ width: "fit-content" }}
                variant="contained"
                onClick={handleDecline}
              >
                Decline
              </Button>
            </div>
            <UserAgreement />
          </div>
        </div>
      </Modal>
    </>
  );
};

const LoginScreen = props => {
  const { user } = useContext(UserContext);
  const { indexedDb } = useContext(DashboardContext);
  const {
    handleLogin,
    loginLoading,
    onChangeHandler,
    oriValue,
    setOriValue,
    handleOriChange,
    login,
  } = props;
  const classes = useStyles();
  const [cookies, , removeCookie] = useCookies();

  const [oris, setOris] = useState([]);

  const removeOriSelectionCookies = () => {
    const paths = ["/", "/mp"];
    paths.forEach(path => {
      removeCookie("mp-last-ori-selection", { path });
    });
  };

  const handleOriChangeWithCookieRemoval = value => {
    removeOriSelectionCookies();
    handleOriChange(value);
  };

  const [getOris, { data, loading, called }] = useLazyQuery(GET_ORIS_QUERY, {
    context: { serializationKey: "incidentQueue" },
    errorPolicy: "all",
    fetchPolicy: "no-cache",
    onCompleted: data => {
      if (data) {
        setOris(data.getOris);
        localStoreData(data, `mp-oris`);
        indexedDb.open();
      }
    },
  });

  useEffect(() => {
    if (!data && !called) getOris();
  }, [getOris, data, called]);

  const _isMounted = useRef(true); // Initial value _isMounted = true

  useEffect(() => {
    return () => {
      // ComponentWillUnmount in Class Component
      _isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (_isMounted.current && oris?.length > 0) {
      const ori = oris?.find(
        ori => ori.id === cookies["mp-last-ori-selection"],
      );
      ori && setOriValue(ori);
    }
  }, [cookies, oris, setOriValue]);

  const trainville = [
    {
      id: "139",
      abbreviation: "TRPD",
      agencyType: "PD",
      oriName: "Trainville Police Department",
      tokenDisabled: false,
      __typename: "Ori",
    },
  ];
  // const showOnlyTrainville = !import.meta.env.VITE_API_HOST.includes("localhost") && !import.meta.env.VITE_API_HOST.includes("demo") && !import.meta.env.VITE_API_HOST.includes("training") && !import.meta.env.VITE_API_HOST.includes("testing") && !import.meta.env.VITE_API_HOST.includes("pilot")
  const showOnlyTrainville = false;
  const oriOneTokenDisabled =
    oris?.find(o => o?.id?.toString() === "1")?.tokenDisabled || false;

  if (user?.isAuthenticated && (props.arg || oris?.length > 0))
    return <Redirect to="/mp/home" />;
  return (
    <div
      id="loginBackground"
      style={{
        backgroundColor: "#0ADDEE",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        width: "100%",
        alignItems: "center",
      }}
    >
      {showOnlyTrainville && (
        <h3 style={{ color: "red" }}>Currently undergoing testing</h3>
      )}

      <div id="login-div">
        <img className={"logo"} alt="Mobile logo" src={MP_logo} />
        {loginLoading && (
          <p
            style={{
              color: "white",
              fontWeight: "bold",
              fontSize: "20px",
              fontFamily: "Roboto, Helvetica, Arial, sans-serif",
              textAlign: "center",
            }}
          >
            Logging you in
          </p>
        )}
        {loading && (
          <p
            style={{
              color: "white",
              fontWeight: "bold",
              fontSize: "20px",
              fontFamily: "Roboto, Helvetica, Arial, sans-serif",
              textAlign: "center",
            }}
          >
            Loading ORI data
          </p>
        )}
        {!loading && !loginLoading && (
          <form
            style={{
              backgroundColor: "white",
              borderRadius: "10px",
              padding: "10px",
              width: "fit-content",
              margin: "auto",
            }}
            onSubmit={handleLogin}
          >
            <FormControl variant="outlined" className={classes.formControl}>
              <Autocomplete
                autoSelect
                id="ori-login-autocomplete"
                options={
                  showOnlyTrainville
                    ? trainville
                    : oris?.filter(ori => ori.agencyType === "PD") || []
                }
                getOptionLabel={option =>
                  option?.oriName + " - " + option?.abbreviation
                }
                getOptionSelected={(option, value) =>
                  option.id === value.id || ""
                }
                getOptionDisabled={option => option?.inactive}
                style={{ width: 300 }}
                value={oriValue}
                renderInput={params => (
                  <TextField
                    {...params}
                    data-cy="ORI-selection-input"
                    inputProps={{
                      ...params.inputProps,
                      "data-cy": "ORI-selection-input-input",
                    }}
                    data-testid="ori-input"
                    label="Select an ORI"
                  />
                )}
                onChange={(e, value) => handleOriChangeWithCookieRemoval(value)}
                autoHighlight
              />
              <TextField
                required
                data-cy="user-name-textfield"
                inputProps={{ "data-cy": "user-name-textfield-input" }}
                className="user-name-input"
                name="userName"
                label="User Name"
                autoComplete="username"
                onChange={onChangeHandler}
                value={login?.userName || ""}
              />
              <TextField
                required
                data-cy="password-textfield"
                inputProps={{ "data-cy": "password-textfield-input" }}
                type="password"
                className="user-password-input"
                label="Password"
                name="password"
                autoComplete="current-password"
                onChange={onChangeHandler}
                value={login?.password || ""}
              />
              {oriOneTokenDisabled === false && (
                <TextField
                  required={!oriValue?.tokenDisabled}
                  data-cy="soft-token-textfield"
                  inputProps={{ "data-cy": "soft-token-textfield-input" }}
                  type="password"
                  name="softToken"
                  className="user-soft-token-input"
                  label="Soft Token"
                  autoComplete="off"
                  onChange={onChangeHandler}
                  value={login?.softToken || ""}
                />
              )}
              <Box
                flexGrow="1"
                display="flex"
                justifyContent="space-between"
                style={{ marginTop: "3vh" }}
                alignItems={"center"}
              >
                <Fab
                  style={{ width: "100%" }}
                  variant="extended"
                  data-cy="login-button"
                  disabled={loginLoading}
                  className="login-button"
                  aria-label="Login"
                  type="submit"
                >
                  {loginLoading ? (
                    <>Logging in...</>
                  ) : (
                    <>
                      <ExitToAppIcon />
                      &nbsp;Login
                    </>
                  )}
                </Fab>
                <HelpIconButton objectname="mobile_help" />
              </Box>
            </FormControl>
          </form>
        )}
        {user?.errorMessage ? (
          <div style={{ marginTop: "1vh", color: "red" }}>
            <p style={{ textAlign: "center" }}>{user?.errorMessage}</p>
          </div>
        ) : null}
        <div></div>
        <div></div>
        <div style={{ textAlign: "center", height: "2vh" }}>
          &#169;{moment().local().format("YYYY")} All Rights Reserved. •
          CrossWind Technologies, Inc.
        </div>
      </div>
    </div>
  );
};

export default Login;
